import React, { useState, useRef } from "react";
import { useStaticQuery, graphql } from "gatsby";
import { useOnClickOutside } from "../../hooks";
import styled from "@emotion/styled";
import tw from "twin.macro";
import AniLink from "gatsby-plugin-transition-link/AniLink";
import { GatsbyImage } from "gatsby-plugin-image";

import Burger from "./Burger";
import OffCanvas from "../OffCanvas/OffCanvas";
import ButtonSolid from "../Button/ButtonSolid";
import ButtonGhost from "../Button/ButtonGhost";
import Accordion from "./Accordion";

const StyledMainNav = styled.nav`
  &[data-fixed="true"] {
    ${tw`fixed top-0 left-0 w-full bg-gray-900 lg:bg-black lg:bg-opacity-70 z-50 before:content before:absolute before:top-0 before:left-0 before:w-full before:h-full before:backdrop-filter before:backdrop-blur-[8px]`}
    #navigation-desktop {
      > li {
        > a {
          ${tw`text-gray-50`}
        }
      }
    }
    .logo-initial {
      ${tw`hidden`}
    }
    .logo-fixed {
      ${tw`block`}
    }
  }
  #navigation-desktop {
    > li {
      > a {
        /* ${({ headerLinkColor }) =>
          headerLinkColor === "white"
            ? tw`text-white hover:text-white`
            : tw`text-gray-900 hover:text-primary-500`}; */
        ${tw`text-gray-50 hover:text-primary-500 font-body text-base uppercase font-bold tracking-widest no-underline pb-12`}
      }
    }
  }

  #navigation-mobile {
    > li {
      > a,
      button {
        ${tw`font-heading text-4xl text-white hover:text-white uppercase no-underline focus:outline-none transition-colors duration-300 ease-linear`}
      }
    }
  }
`;

const MainNav = ({
  headerStyle,
  headerHasBorder,
  headerLinkColor,
  scrolled,
}) => {
  // determine if offcanvas is open
  const [offcanvasOpen, setOffcanvasOpen] = useState(false);

  const [subMenuHovering1, setSubMenuHovering1] = useState(false);
  const isHoveringSubMenu1 = () => setSubMenuHovering1(true);
  const notHoveringSubMenu1 = () => setSubMenuHovering1(false);

  const [subMenuHovering2, setSubMenuHovering2] = useState(false);
  const isHoveringSubMenu2 = () => setSubMenuHovering2(true);
  const notHoveringSubMenu2 = () => setSubMenuHovering2(false);

  // handle click of navigation items
  const clickHandler = () => {
    setOffcanvasOpen(!offcanvasOpen);
  };

  // close offcanvas onclick outside
  const node = useRef();
  useOnClickOutside(node, () => setOffcanvasOpen(false));

  const data = useStaticQuery(graphql`
    {
      darkLogo: file(relativePath: { eq: "global/Logo.png" }) {
        childImageSharp {
          gatsbyImageData(layout: CONSTRAINED, width: 78, placeholder: NONE)
        }
      }
      lightLogo: file(relativePath: { eq: "global/Logo.png" }) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH, placeholder: NONE)
        }
      }
    }
  `);

  // Define logos based on header style
  let initialLogo = null,
    stickyLogo = null,
    className = null;

  if (headerStyle === "overlap" || headerStyle === "overlap-hero") {
    initialLogo = data.lightLogo.childImageSharp.gatsbyImageData;
    stickyLogo = data.darkLogo.childImageSharp.gatsbyImageData;
    className = "absolute";
  } else {
    initialLogo = data.darkLogo.childImageSharp.gatsbyImageData;
    stickyLogo = data.darkLogo.childImageSharp.gatsbyImageData;
  }

  if (offcanvasOpen) {
    initialLogo = data.darkLogo.childImageSharp.gatsbyImageData;
    stickyLogo = data.darkLogo.childImageSharp.gatsbyImageData;
  }

  const navigation = {
    services: [
      {
        name: "All Services",
        href: "/services/",
      },
      {
        name: "Resort Cleaning",
        href: "/resort-cleaning/",
      },
      {
        name: "Restaurant Cleaning",
        href: "/restaurant-cleaning/",
      },
      {
        name: "Commercial Kitchen Cleaning",
        href: "/commercial-kitchen-cleaning/",
      },
      {
        name: "Spa Cleaning",
        href: "/spa-cleaning/",
      },
      {
        name: "Commercial Cleaning",
        href: "/commercial-cleaning/",
      },
      {
        name: "Hard Surface Floor Care",
        href: "/hard-surface-floor-care/",
      },
      {
        name: "Hotel Disinfecting",
        href: "/hotel-disinfecting/",
      },
      {
        name: "Hotel Housekeeping",
        href: "/hotel-housekeeping/",
      },
      {
        name: "Hotel & Hospitality Staffing",
        href: "/hospitality-staffing/",
      },
      {
        name: "Restoration Services",
        href: "/hotel-restoration/",
      },
    ],
    about: [
      {
        name: "About",
        href: "/about/",
      },
      {
        name: "Reviews",
        href: "/reviews/",
      },
      {
        name: "Careers",
        href: "/careers/",
      },
      {
        name: "Sustainability",
        href: "/sustainability/",
      },
      {
        name: "Blog",
        href: "/blog/",
      },
    ],
    // social: [
    //   {
    //     name: "Facebook",
    //     href: "https://www.facebook.com/hotelcleaningservices",
    //     icon: "fab fa-facebook-f",
    //   },
    //   {
    //     name: "Linkedin",
    //     href: "https://www.linkedin.com/company/hotel-services/about/",
    //     icon: "fab fa-linkedin-in",
    //   },
    //   {
    //     name: "Yelp",
    //     href: "#",
    //     icon: "fab fa-yelp",
    //   },
    // ],
  };

  return (
    <StyledMainNav
      id="main-navigation"
      className={`py-3.5 lg:py-4 bg-gray-900 lg:hover:bg-black lg:hover:bg-opacity-70 w-full transition duration-300 ease-linear border-b lg:border-none border-solid border-primary-500 ${
        headerStyle === "overlap" ? "lg:bg-transparent" : "lg:bg-gray-900"
      } ${headerHasBorder && "border-b border-solid border-primary-500"} ${
        offcanvasOpen ? "" : ""
      } ${className}`}
      role="navigation"
      aria-label="main-navigation"
      data-fixed={scrolled}
      headerStyle={headerStyle}
      headerHasBorder={headerHasBorder}
      headerLinkColor={headerLinkColor}
      offcanvasOpen={offcanvasOpen}
    >
      <div className="container relative flex justify-between items-center">
        <div className="flex-auto flex items-center lg:hidden">
          <a href="tel:310-831-0587" className="group z-30">
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="z-30"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M3.62 7.79C5.06 10.62 7.38 12.93 10.21 14.38L12.41 12.18C12.68 11.91 13.08 11.82 13.43 11.94C14.55 12.31 15.76 12.51 17 12.51C17.55 12.51 18 12.96 18 13.51V17C18 17.55 17.55 18 17 18C7.61 18 0 10.39 0 1C0 0.45 0.45 0 1 0H4.5C5.05 0 5.5 0.45 5.5 1C5.5 2.25 5.7 3.45 6.07 4.57C6.18 4.92 6.1 5.31 5.82 5.59L3.62 7.79Z"
                className={`fill-current group-hover:text-primary-400 transition-colors duration-300 ease-linear transform ${
                  headerStyle === "overlap"
                    ? "text-primary-500"
                    : "text-primary-500"
                } ${offcanvasOpen || scrolled ? "text-primary-500" : null}`}
              />
            </svg>
          </a>
        </div>
        <div className="flex-auto flex items-center justify-center lg:justify-start lg:space-x-8 absolute lg:relative inset-x-0 top-2 lg:top-0 z-20">
          <AniLink
            fade
            to="/"
            className="flex items-center justify-center w-20 h-20 lg:w-auto lg:h-auto rounded-full lg:rounded-none bg-gray-900 lg:bg-transparent border lg:border-none border-primary-500"
          >
            <div className="lg:hidden absolute flex items-center justify-center w-18 h-18 rounded-full border border-primary-500"></div>
            <div className="logo-initial">
              <GatsbyImage
                image={initialLogo}
                alt="Hotel Cleaning Services Logo"
                className="w-[58px] md:w-[78px]"
              />
            </div>
            <div className="logo-fixed hidden">
              <GatsbyImage
                image={stickyLogo}
                alt="Hotel Cleaning Services Logo"
                className="w-[58px] md:w-[78px]"
              />
            </div>

            <span className="font-heading text-2xl uppercase ml-4 hidden lg:inline">
              Hotel Cleaning Services
            </span>
          </AniLink>
        </div>
        <div className="flex items-center justify-end flex-auto">
          <ul
            id="navigation-desktop"
            className="hidden lg:flex lg:flex-row lg:space-x-7 lg:items-center lg:justify-end lg:mr-10"
          >
            <li
              className={`group relative ${subMenuHovering1 ? "active" : ""}`}
            >
              <AniLink
                fade
                to="/services/"
                onMouseEnter={isHoveringSubMenu1}
                onMouseLeave={notHoveringSubMenu1}
              >
                Services
              </AniLink>
              <ul className="absolute top-0 bg-black bg-opacity-70 backdrop-filter backdrop-blur-[8px] flex flex-col w-auto py-4 invisible group-hover:visible transform -translate-x-8 translate-y-20 group-hover:translate-y-16 opacity-0 group-hover:opacity-100 px-7 pt-7 pb-10 z-10 transition-all duration-300 ease-linear">
                {navigation.services.slice(1).map((item) => (
                  <li
                    key={item.name}
                    className="whitespace-nowrap border-b border-gray-200 border-opacity-20 py-2"
                  >
                    <AniLink
                      fade
                      to={item.href}
                      className="relative block text-base tracking-wider text-gray-50 hover:text-primary-500 no-underline"
                    >
                      {item.name}
                    </AniLink>
                  </li>
                ))}
              </ul>
            </li>

            <li
              className={`group relative ${subMenuHovering2 ? "active" : ""}`}
            >
              <AniLink
                fade
                to="/about/"
                onMouseEnter={isHoveringSubMenu2}
                onMouseLeave={notHoveringSubMenu2}
              >
                About
              </AniLink>
              <ul className="absolute top-0 bg-black bg-opacity-70 backdrop-filter backdrop-blur-[8px] flex flex-col w-auto py-4 invisible group-hover:visible transform -translate-x-8 translate-y-20 group-hover:translate-y-16 opacity-0 group-hover:opacity-100 px-7 pt-7 pb-10 z-10 transition-all duration-300 ease-linear">
                {navigation.about.slice(1).map((item) => (
                  <li
                    key={item.name}
                    className="whitespace-nowrap border-b border-gray-200 border-opacity-20 py-2"
                  >
                    <AniLink
                      fade
                      to={item.href}
                      className="relative block text-base tracking-wider text-gray-50 hover:text-primary-500 no-underline"
                    >
                      {item.name}
                    </AniLink>
                  </li>
                ))}
              </ul>
            </li>
          </ul>

          <div className="hidden lg:inline-flex lg:items-center lg:space-x-10">
            <ButtonSolid
              modal="modal-contact"
              text="Contact"
              className="min-w-[192px]"
            />
          </div>

          <div className="lg:hidden" ref={node}>
            <Burger
              offcanvasOpen={offcanvasOpen}
              setOffcanvasOpen={setOffcanvasOpen}
              headerStyle={headerStyle}
              scrolled={scrolled}
              aria-controls="offcanvas-navigation"
            />
            <OffCanvas offcanvasOpen={offcanvasOpen} id="offcanvas-navigation">
              <ul
                id="navigation-mobile"
                className="flex flex-col space-y-4 mb-7"
              >
                <li className="border-b border-primary-500 pb-4">
                  <Accordion title="Services" className="submenu-parent">
                    {navigation.services.map((item) => (
                      <li
                        key={item.name}
                        className="whitespace-nowrap border-b border-gray-200 border-opacity-20 py-2"
                      >
                        <AniLink
                          fade
                          to={item.href}
                          onKeyDown={clickHandler}
                          onClick={clickHandler}
                          className="font-body text-gray-50 tracking-wider no-underline"
                        >
                          {item.name}
                        </AniLink>
                      </li>
                    ))}
                  </Accordion>
                </li>
                <li className="border-b border-primary-500 pb-4">
                  <Accordion title="About" className="submenu-parent">
                    {navigation.about.map((item) => (
                      <li key={item.name}>
                        <AniLink
                          fade
                          to={item.href}
                          onKeyDown={clickHandler}
                          onClick={clickHandler}
                          className="font-body text-gray-50 tracking-wider no-underline"
                        >
                          {item.name}
                        </AniLink>
                      </li>
                    ))}
                  </Accordion>
                </li>
              </ul>
              {/* 
              <div className="flex flex-row justify-center space-x-4 mb-10">
                {navigation.social.map((item) => (
                  <a
                    key={item.name}
                    href={item.href}
                    target="_blank"
                    rel="noreferrer"
                    className="text-gray-900 hover:text-white bg-transparent hover:bg-gray-900 rounded-full w-8 h-8 flex items-center justify-center border border-gray-900 border-opacity-40 no-underline"
                  >
                    <span className="sr-only">{item.name}</span>
                    <i
                      className={`text-base ${item.icon}`}
                      aria-hidden="true"
                    ></i>
                  </a>
                ))}
              </div> */}

              <div className="grid gap-y-4">
                <ButtonGhost modal="modal-contact" text="Contact Us" />
              </div>
            </OffCanvas>
          </div>
        </div>
      </div>
    </StyledMainNav>
  );
};
export default MainNav;
